<script setup lang="ts">
  import { Icon, IconName, IconSize, Input, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, ref, watch } from 'vue';
  import { Field, FieldSize } from '@/entities/searchForm';
  import { useOutsidePointer } from '@/shared/composables';
  import type { DepartureCity } from '@/shared/model';
  import DepartureCityDropdown from './DepartureCityDropdown.vue';
  import { DEPARTURE_CITY_TEXTS } from '../config/texts';
  import { useDepartureCity } from '../model/useDepartureCity';

  interface DepartureCityFieldProps {
    modelValue?: DepartureCity | null;
    cities?: DepartureCity[];
    disabled?: boolean;
    size?: `${FieldSize}`;
  }

  const props = withDefaults(defineProps<DepartureCityFieldProps>(), {
    modelValue: null,
    cities: () => [],
    disabled: false,
    size: FieldSize.M,
  });

  interface DepartureCityFieldEmits {
    (e: 'update:modelValue', city: DepartureCity | null): void;
  }

  const emit = defineEmits<DepartureCityFieldEmits>();

  const model = computed<DepartureCity | null>({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
  });

  const departure = useDepartureCity({ model, initialCities: props.cities ?? [] });

  watch(
    () => props.cities,
    (list) => departure.setCities(list ?? []),
  );

  const inputRef = ref<{ focus: () => void } | null>(null);

  const focusInput = (): void => {
    inputRef.value?.focus();
  };

  const onOpen = (): void => {
    departure.open();
  };

  const onSelect = (city: DepartureCity): void => {
    departure.selectCity(city);
  };

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      departure.close();
    }
  };

  const onClear = (): void => {
    departure.clear();
  };

  const rootRef = ref<HTMLElement | null>(null);

  useOutsidePointer(rootRef, { active: departure.controlActivated, handler: departure.close });

  defineExpose({
    validate: (): boolean => departure.validate(),
    clear: (): void => departure.clear(),
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    ref="rootRef"
    class="departure-city-field"
    data-testid="departure-city-field"
  >
    <DepartureCityDropdown
      :options="departure.optionsList.value"
      :selected-id="departure.selection.value?.id ?? null"
      :visible="departure.controlActivated.value"
      @select="onSelect"
      @close="departure.close"
    >
      <template #reference>
        <Field
          variant="input"
          :label="DEPARTURE_CITY_TEXTS.fieldLabelFixed"
          :active="departure.controlActivated.value"
          :disabled="disabled"
          @click="focusInput"
        >
          <template #value>
            <Input
              ref="inputRef"
              :model-value="departure.inputString.value"
              placeholder=""
              :error="departure.invalid.value"
              :bg="false"
              :disabled="disabled"
              :bordered="false"
              size="xs"
              name="input"
              data-testid="departure-city-input"
              class="departure-city-field__input"
              @update:modelValue="departure.setInput"
              @focus="onOpen"
              @click="onOpen"
              @keydown.native="onKeydown"
            >
              <template #append>
                <button
                  v-if="departure.clearVisible.value"
                  type="button"
                  class="departure-city-field__clear"
                  data-testid="departure-city-clear"
                  aria-label="Очистить"
                  @click.stop.prevent="onClear"
                >
                  <Icon
                    :name="IconName.Close"
                    :size="IconSize.S"
                  />
                </button>
              </template>
            </Input>
          </template>
        </Field>
      </template>
    </DepartureCityDropdown>

    <div
      v-if="departure.errorText.value"
      class="departure-city-field__error"
      data-testid="departure-city-error"
    >
      <Typo
        :variant="TypoVariant.BodySRegular"
        tag="span"
      >
        {{ departure.errorText.value }}
      </Typo>
    </div>
  </div>
</template>

<style scoped lang="scss">
  ::v-deep .departure-city-field__input input[name='input'] {
    font-size: 16px;
  }

  .departure-city-field {
    position: relative;
    width: 100%;

    ::v-deep .tl-input {
      padding: 0;
    }

    &__clear {
      align-items: center;
      background: none;
      border: none;
      color: var(--color-text-secondary);
      cursor: pointer;
      display: inline-flex;
      justify-content: center;
      padding: 0;
    }

    &__error {
      color: var(--color-error-500);
      margin-top: var(--spacing-2xs);
    }
  }
</style>
